import React from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

interface BoundaryProps {
  children: React.ReactNode;
  moduleName?: string;
  /** The error clears when this changes (the URL), without remounting the module */
  resetKey: string;
}

interface BoundaryState {
  error: Error | null;
}

class Boundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[${this.props.moduleName ?? 'Module'}] page crashed:`, error, info.componentStack);
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    // A page chunk that no longer exists (new deploy while the tab was open) needs a full reload
    const isChunkError = /dynamically imported module|Failed to fetch|Importing a module script failed/i.test(this.state.error.message);

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
        <AlertTriangle className="w-14 h-14 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
          {isChunkError ? 'A new version is available' : 'This page ran into a problem'}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">
          {isChunkError
            ? 'Please reload to get the latest version.'
            : 'The rest of the app still works — open another page from the menu, or try again.'}
        </p>
        <div className="mt-6 flex gap-2">
          {!isChunkError && (
            <button onClick={() => this.setState({ error: null })} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
              Try again
            </button>
          )}
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Error boundary around one module: a crash in a page shows this message inside the layout,
 * so the sidebar / header (and other modules) keep working. Clears itself on navigation.
 */
export function ModuleErrorBoundary({ children, moduleName }: { children: React.ReactNode; moduleName?: string }) {
  const { pathname } = useLocation();
  return (
    <Boundary moduleName={moduleName} resetKey={pathname}>
      {children}
    </Boundary>
  );
}
